import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Row,
  Col,
  Table,
  Input,
  Button,
  Badge,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import AppLoader from "../../../components/Common/AppLoader";
import { getUserActivityLogsApi } from "../../../apiServices/userActivityServices";
import useUsersWithLeads from "../../../Hooks/useUsersWithLeads";

const UserActivityLogs = () => {
  const navigate = useNavigate();
  const { users = [] } = useUsersWithLeads();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uniqueActions, setUniqueActions] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  });

  const [filters, setFilters] = useState({
    search: "",
    userId: "",
    action: "",
    startDate: "",
    endDate: "",
    page: 1,
    limit: 20,
  });

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const res = await getUserActivityLogsApi(filters);
      const data = res?.data || {};
      setLogs(data?.records || []);
      setUniqueActions(data?.uniqueActions || []);
      setPagination({
        page: data?.page || 1,
        limit: data?.limit || 20,
        total: data?.total || 0,
        totalPages: data?.totalPages || 1,
      });
    } catch (err) {
      console.error("Failed to fetch user activity logs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchLogs();
    }, 300);
    return () => clearTimeout(delay);
  }, [JSON.stringify(filters)]);

  const handleChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value, page: 1 }));
  };

  const handleReset = () => {
    setFilters({
      search: "",
      userId: "",
      action: "",
      startDate: "",
      endDate: "",
      page: 1,
      limit: 20,
    });
  };

  // Group consecutive repetitions of the same log type/user/lead/field
  const groupConsecutiveLogs = (records = []) => {
    if (!records.length) return [];

    const grouped = [];
    let currentGroup = null;

    for (const item of records) {
      const userId = item.changedBy?._id || item.changedBy || item.changedByName || "system";
      const act = item.action || item.field || "activity";
      const leadRef = item.leadId || "";
      const valRef = String(item.newValue || item.newValueDisplay || "");
      const key = `${userId}_${act}_${item.field || ""}_${leadRef}_${valRef}`;

      if (currentGroup && currentGroup.key === key) {
        currentGroup.count += 1;
      } else {
        if (currentGroup) {
          grouped.push(currentGroup);
        }
        currentGroup = {
          ...item,
          key,
          count: 1,
        };
      }
    }

    if (currentGroup) {
      grouped.push(currentGroup);
    }

    return grouped;
  };

  const formattedLogs = groupConsecutiveLogs(logs);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return { date: "-", time: "-" };
    const d = new Date(dateStr);
    return {
      date: d.toLocaleDateString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
      time: d.toLocaleTimeString("en-IN", {
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    };
  };

  const formatTitleCase = (str = "") => {
    if (!str) return "";
    return str
      .replaceAll("_", " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getActionBadge = (act = "", field = "", count = 1) => {
    const rawAction = act || field || "activity";
    const actionLower = rawAction.toLowerCase();
    let color = "info";
    let icon = "mdi-eye-outline";

    if (actionLower.includes("stage")) {
      color = "primary";
      icon = "mdi-pulse";
    } else if (actionLower.includes("remark")) {
      color = "warning";
      icon = "mdi-comment-edit-outline";
    } else if (actionLower.includes("call")) {
      color = "success";
      icon = "mdi-phone";
    } else if (actionLower.includes("whatsapp")) {
      color = "success";
      icon = "mdi-whatsapp";
    } else if (actionLower.includes("unlock")) {
      color = "danger";
      icon = "mdi-lock-open-outline";
    } else if (actionLower.includes("assign")) {
      color = "info";
      icon = "mdi-account-switch";
    } else if (actionLower.includes("view")) {
      color = "info";
      icon = "mdi-eye-outline";
    }

    const labelText = formatTitleCase(rawAction);

    return (
      <div className="d-inline-flex align-items-center gap-1">
        <Badge
          color={color}
          pill
          className={`px-3 py-2 ${color === "warning" ? "text-dark" : "text-white"}`}
          style={{ fontSize: "12px", fontWeight: "600" }}
        >
          <i className={`mdi ${icon} me-1`} />
          {labelText}
        </Badge>
        {count > 1 && (
          <Badge
            color="dark"
            pill
            className="px-2 py-1 fs-6 fw-bold"
            title={`${count} consecutive repeated actions`}
          >
            {count}x
          </Badge>
        )}
      </div>
    );
  };

  return (
    <div className="page-content">
      <Breadcrumbs title="Team" breadcrumbItem="User Activity Logs" />

      {/* STAT CARDS */}
      <Row className="g-3 mb-3">
        <Col md="4">
          <Card className="border-0 shadow-sm h-100">
            <CardBody className="d-flex align-items-center justify-content-between p-3">
              <div>
                <small className="text-muted fw-semibold d-block mb-1">Total Logged Activities</small>
                <h3 className="mb-0 fw-bold">{pagination.total || 0}</h3>
              </div>
              <div className="avatar-sm rounded-circle bg-primary bg-subtle text-primary d-flex align-items-center justify-content-center fs-3">
                <i className="mdi mdi-history" />
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col md="4">
          <Card className="border-0 shadow-sm h-100">
            <CardBody className="d-flex align-items-center justify-content-between p-3">
              <div>
                <small className="text-muted fw-semibold d-block mb-1">Grouped Entries</small>
                <h3 className="mb-0 fw-bold">{formattedLogs.length}</h3>
              </div>
              <div className="avatar-sm rounded-circle bg-info bg-subtle text-info d-flex align-items-center justify-content-center fs-3">
                <i className="mdi mdi-format-list-bulleted" />
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col md="4">
          <Card className="border-0 shadow-sm h-100">
            <CardBody className="d-flex align-items-center justify-content-between p-3">
              <div>
                <small className="text-muted fw-semibold d-block mb-1">Action Types</small>
                <h3 className="mb-0 fw-bold">{uniqueActions.length}</h3>
              </div>
              <div className="avatar-sm rounded-circle bg-success bg-subtle text-success d-flex align-items-center justify-content-center fs-3">
                <i className="mdi mdi-thunder-outline" />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* FILTER BAR */}
      <Card className="p-3 mb-3 shadow-sm border-0">
        <Row className="g-2 align-items-center">
          <Col md="3" sm="6">
            <div className="position-relative">
              <i
                className="mdi mdi-magnify position-absolute"
                style={{ top: "10px", left: "12px", fontSize: "16px", color: "#74788d" }}
              />
              <Input
                placeholder="Search action / comment..."
                value={filters.search}
                style={{ paddingLeft: "35px" }}
                onChange={(e) => handleChange("search", e.target.value)}
              />
            </div>
          </Col>

          <Col md="2" sm="6">
            <Input
              type="select"
              value={filters.action}
              onChange={(e) => handleChange("action", e.target.value)}
            >
              <option value="">All Action Types</option>
              {uniqueActions.map((act) => (
                <option key={act} value={act}>
                  {formatTitleCase(act)}
                </option>
              ))}
            </Input>
          </Col>

          <Col md="2" sm="6">
            <Input
              type="select"
              value={filters.userId}
              onChange={(e) => handleChange("userId", e.target.value)}
            >
              <option value="">All Users</option>
              {users.map((u) => (
                <option key={u._id} value={u._id}>
                  {u.firstName} {u.lastName}
                </option>
              ))}
            </Input>
          </Col>

          <Col md="2" sm="6">
            <Input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleChange("startDate", e.target.value)}
            />
          </Col>

          <Col md="2" sm="6">
            <Input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleChange("endDate", e.target.value)}
            />
          </Col>

          <Col md="1" className="d-flex gap-1 justify-content-end">
            <Button outline color="secondary" title="Reset Filters" onClick={handleReset}>
              <i className="mdi mdi-refresh" />
            </Button>
          </Col>
        </Row>
      </Card>

      {/* TABLE / CONTENT */}
      <Card className="border-0 shadow-sm">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0 fs-5 fw-bold">User Activity Log History</h4>
            <div className="d-flex align-items-center gap-2">
              <span className="small text-muted">Per Page:</span>
              <Input
                type="select"
                size="sm"
                value={filters.limit}
                style={{ width: "80px" }}
                onChange={(e) => handleChange("limit", e.target.value)}
              >
                {[10, 20, 50, 100].map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </Input>
            </div>
          </div>

          {loading ? (
            <div className="py-5 text-center">
              <AppLoader compact />
            </div>
          ) : !formattedLogs.length ? (
            <div className="py-5 text-center text-muted">No User Activity Logs Found</div>
          ) : isMobile ? (
            <div className="d-flex flex-column gap-3">
              {formattedLogs.map((log) => {
                const dt = formatDateTime(log.createdAt);
                const hasLead = Boolean(log.leadId);
                return (
                  <Card
                    key={log.key || log._id}
                    className="border shadow-none mb-0"
                    onClick={() => {
                      if (hasLead) {
                        navigate(`/lead-details/${log.source || "facebook"}/${log.leadId}`);
                      }
                    }}
                    style={{ cursor: hasLead ? "pointer" : "default" }}
                  >
                    <CardBody className="p-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold"
                            style={{ width: "36px", height: "36px", fontSize: "14px" }}
                          >
                            {log.changedByName?.charAt(0) || "U"}
                          </div>
                          <div>
                            <div className="fw-semibold">{log.changedByName}</div>
                            <small className="text-muted">{log.userEmail}</small>
                          </div>
                        </div>
                        {getActionBadge(log.action, log.field, log.count)}
                      </div>

                      {(log.oldValueDisplay || log.newValueDisplay) && (
                        <div className="bg-light p-2 rounded small mb-2">
                          {log.oldValueDisplay && (
                            <div className="text-muted">Old: {log.oldValueDisplay}</div>
                          )}
                          {log.newValueDisplay && (
                            <div className="fw-semibold text-dark">New: {log.newValueDisplay}</div>
                          )}
                        </div>
                      )}

                      {log.comment && (
                        <div className="small text-muted fst-italic mb-2">
                          "{log.comment}"
                        </div>
                      )}

                      <div className="d-flex justify-content-between align-items-center small text-muted">
                        <div>
                          <i className="mdi mdi-clock-outline me-1" />
                          {dt.date} {dt.time}
                        </div>
                        {hasLead && (
                          <span className="text-primary fw-semibold">
                            View Lead <i className="mdi mdi-arrow-right" />
                          </span>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="table align-middle table-hover" style={{ fontSize: "13px" }}>
                <thead className="table-light">
                  <tr>
                    <th>User</th>
                    <th>Action</th>
                    <th>Details / Field</th>
                    <th>Timestamp (IST)</th>
                    <th>Lead Details</th>
                  </tr>
                </thead>
                <tbody>
                  {formattedLogs.map((log) => {
                    const dt = formatDateTime(log.createdAt);
                    const hasLead = Boolean(log.leadId);
                    return (
                      <tr
                        key={log.key || log._id}
                        onClick={() => {
                          if (hasLead) {
                            navigate(`/lead-details/${log.source || "facebook"}/${log.leadId}`);
                          }
                        }}
                        style={{ cursor: hasLead ? "pointer" : "default" }}
                      >
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div
                              className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold"
                              style={{ width: "35px", height: "35px", fontSize: "13px" }}
                            >
                              {log.changedByName?.charAt(0) || "U"}
                            </div>
                            <div>
                              <div className="fw-semibold">{log.changedByName}</div>
                              <small className="text-muted">{log.userEmail}</small>
                            </div>
                          </div>
                        </td>

                        <td>{getActionBadge(log.action, log.field, log.count)}</td>

                        <td>
                          {log.field && (
                            <div className="small text-muted text-uppercase fw-semibold mb-1">
                              FIELD: {log.field}
                            </div>
                          )}
                          {log.oldValueDisplay && (
                            <div className="small text-muted">Old: {log.oldValueDisplay}</div>
                          )}
                          {log.newValueDisplay && (
                            <div className="small fw-semibold text-dark">
                              New: {log.newValueDisplay}
                            </div>
                          )}
                          {log.comment && (
                            <div className="small text-secondary fst-italic">
                              Comment: "{log.comment}"
                            </div>
                          )}
                        </td>

                        <td>
                          <div className="fw-semibold">{dt.date}</div>
                          <small className="text-muted">
                            <i className="mdi mdi-clock-outline me-1" />
                            {dt.time}
                          </small>
                        </td>

                        <td>
                          {hasLead ? (
                            <Link
                              to={`/lead-details/${log.source || "facebook"}/${log.leadId}`}
                              className="btn btn-sm btn-soft-primary px-3 rounded-pill"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <i className="mdi mdi-open-in-new me-1" /> View Lead
                            </Link>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}

          {/* PAGINATION */}
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
            <div className="small text-muted">
              Showing page <strong>{pagination.page}</strong> of{" "}
              <strong>{pagination.totalPages}</strong> (Total <strong>{pagination.total}</strong> records)
            </div>

            <div className="d-flex align-items-center gap-2">
              <Button
                size="sm"
                color="light"
                disabled={pagination.page <= 1}
                onClick={() => setFilters((p) => ({ ...p, page: p.page - 1 }))}
              >
                <i className="mdi mdi-chevron-left me-1" /> Previous
              </Button>

              <Badge color="primary" className="px-3 py-2 fs-6">
                {pagination.page} / {pagination.totalPages}
              </Badge>

              <Button
                size="sm"
                color="light"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setFilters((p) => ({ ...p, page: p.page + 1 }))}
              >
                Next <i className="mdi mdi-chevron-right ms-1" />
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default UserActivityLogs;
